import {
  ItemListCarouselItem,
  ItemListCarouselItemVariant,
  ItemListCarouselRowData,
} from "@/components/item-list/types";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import styled from "@emotion/styled";
import {
  MDSFadeHorizontalScroll,
  MDSText,
  MDSTextColor,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { Button } from "react-aria-components";

export interface ItemListCarouselRowProps {
  data: ItemListCarouselRowData;
}

export const ItemListCarouselRow = observer<ItemListCarouselRowProps>(({ data }) => {
  return (
    <StyledFadeHorizontalScroll>
      {data.payload.items
        .filter(item => !item.isHidden)
        .map(item => (
          <ItemListCarouselRowItem key={item.id} {...item} />
        ))}
    </StyledFadeHorizontalScroll>
  );
});

const ItemListCarouselRowItem: FC<ItemListCarouselItem> = ({
  id: itemId,
  icon,
  primaryLabel,
  secondaryLabel,
  variant,
  onClick,
}) => {
  return (
    <StyledButton variant={variant} onPress={() => onClick({ itemId })}>
      <IconWrapper>{icon()}</IconWrapper>
      <CenterVertically>
        <StyledText
          variant={MDSTextVariant.TextM}
          numberOfLines={1}
          overflow={MDSTextOverflow.Ellipsis}
          weight={MDSTextWeight.Medium}
          color={MDSTextColor.Contrast}
        >
          {primaryLabel}
        </StyledText>
        {secondaryLabel && (
          <StyledText
            variant={MDSTextVariant.TextS}
            numberOfLines={1}
            overflow={MDSTextOverflow.Ellipsis}
            color={MDSTextColor.Primary}
          >
            {secondaryLabel}
          </StyledText>
        )}
      </CenterVertically>
    </StyledButton>
  );
};

const StyledFadeHorizontalScroll = styled(MDSFadeHorizontalScroll)`
  gap: ${({ theme }) => theme.spacing["4"]};
  padding-inline: ${({ theme }) => theme.spacing["7"]};
  max-width: 100%;
  overflow: scroll;
`;

const StyledButton = styled(Button)<{ variant?: ItemListCarouselItemVariant }>`
  position: relative;
  display: flex;
  max-width: 240px;
  flex-shrink: 0;
  overflow: hidden;
  gap: ${({ theme }) => theme.spacing["2"]};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing["4"]};
  background-color: ${({ theme }) => theme.colors.background.button.chrome.default};
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.chrome.hover};
  }
`;

const StyledText = styled(MDSText)`
  text-align: left;
  width: 100%;
`;

const IconWrapper = styled.div<{ variant?: ItemListCarouselItemVariant }>`
  height: 16px;
  width: 16px;
  padding-top: ${({ theme }) => theme.spacing["0.5"]};
`;

const CenterVertically = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;
